// Buyer Master — manage buyers, the BRANDS under each buyer, and the performance
// TEAM assigned per brand (Buyer → Brand → Team model). The brand team mirrors
// the GPO team's 5 role columns; GPOs resolve their team via their brand.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { C } from '../theme';
import { ListFooter } from '../ui';

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 10, color: '#888', fontWeight: 700, display: 'block', marginBottom: 3, textTransform: 'uppercase' };

// MODULE SCOPE, DELIBERATELY — DO NOT MOVE THESE BACK INSIDE THE COMPONENT.
// Declared in the render body they were a NEW COMPONENT TYPE on every render, so React
// unmounted and remounted their whole subtree on each keystroke and the focused input went
// with it. Rajan lost focus typing a tolerance. Third instance of this in the repo, after
// HuddleGrid's Tray and HelpSlip's Row.
//
// Note what makes it bite: neither of these renders an input. They render {children}, and the
// children are the inputs. A wrapper is enough — which is why an audit that looked for nested
// components CONTAINING a text input would not have found either of them.
const Row = ({ children }) => <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>{children}</div>;
const Head = ({ children }) => <div style={{ fontSize: 11, fontWeight: 800, color: C.navy, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 6 }}>{children}</div>;
const btn = (bg, col, br) => ({ padding: '7px 14px', background: bg, color: col, border: br || 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' });

const ROLES = [
  ['merchandiser_id', 'Merchandiser', 'merchandiser', 'merchandiser_name'],
  ['procurement_officer_id', 'Procurement Officer', 'procurement', 'procurement_officer_name'],
  ['quality_incharge_id', 'Quality Incharge', 'quality', 'quality_incharge_name'],
  ['ie_officer_id', 'IE Officer', 'ie', 'ie_officer_name'],
  ['production_incharge_id', 'Production Incharge', 'production', 'production_incharge_name'],
];
const SLOTS = ['merchandiser', 'procurement', 'quality', 'ie', 'production'];

const Modal = ({ title, onClose, children, width = 460 }) => (
  <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,20,40,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', overflowY: 'auto' }}>
    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: width }}>
      <div style={{ background: C.sidebar, padding: '14px 22px', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>✕</button>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  </div>
);

export default function BuyerMaster() {
  const [buyers, setBuyers] = useState([]);
  const [sel, setSel] = useState(null);          // selected buyer
  const [brands, setBrands] = useState([]);
  const [slotUsers, setSlotUsers] = useState({});   // slot -> users (per-slot role filtered)
  const [err, setErr] = useState('');

  const [buyerModal, setBuyerModal] = useState(null);   // {} new / buyer edit
  const [note, setNote] = useState('');                 // a warning the save came back with
  const [brandModal, setBrandModal] = useState(null);   // {} new / brand edit
  const [teamModal, setTeamModal] = useState(null);     // { brand, form }

  const loadBuyers = useCallback(() => api.get('/buyers').then(r => setBuyers(r.data.data || [])).catch(() => {}), []);
  const loadBrands = useCallback((buyerId) => { if (!buyerId) return setBrands([]); api.get('/brands?buyer_id=' + buyerId).then(r => setBrands(r.data.data || [])).catch(() => setBrands([])); }, []);
  useEffect(() => { loadBuyers(); Promise.all(SLOTS.map(s => api.get('/performance/assignable-users?slot=' + s).then(r => [s, r.data.data || []]).catch(() => [s, []]))).then(p => setSlotUsers(Object.fromEntries(p))); }, [loadBuyers]);
  useEffect(() => { if (sel) loadBrands(sel.id); }, [sel, loadBrands]);

  // Which tolerance fields moved, so the reason box appears only when the route will ask for
  // one. `orig` is the row as loaded; comparing against it is the same test the route runs.
  const tolMoved = (m) => !!m && ['tolerance_over_pct', 'tolerance_under_pct']
    .some(f => String(m[f] ?? '') !== String((m.__orig || {})[f] ?? ''));

  const saveBuyer = async () => {
    setErr(''); setNote('');
    try {
      // __orig is a client-side copy for the dirty check — never sent. The route ignores keys
      // outside its editable list, but posting a field that is not a column reads as an
      // attempt to write one.
      const { __orig, ...payload } = buyerModal;
      if (buyerModal.id) {
        const r = await api.put('/buyers/' + buyerModal.id, payload);
        // READ THE WARNING BACK. Lowering a band returns a count of lines already ordered
        // above the new figure; the response carried it and this screen used to discard it,
        // so the one case the warning exists for was invisible.
        if (r.data.warning) { setNote(r.data.warning); setBuyerModal(null); loadBuyers(); return; }
      } else await api.post('/buyers', payload);
      setBuyerModal(null); loadBuyers();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save buyer'); }
  };
  const saveBrand = async () => {
    setErr('');
    try {
      if (brandModal.id) await api.patch('/brands/' + brandModal.id, { name: brandModal.name });
      else await api.post('/brands', { name: brandModal.name, buyer_id: sel.id });
      setBrandModal(null); loadBrands(sel.id);
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save brand'); }
  };
  const openTeam = async (brand) => {
    const t = (await api.get('/brands/' + brand.id + '/team').catch(() => ({ data: { data: null } }))).data.data || {};
    setTeamModal({ brand, row: t, form: ROLES.reduce((o, [k]) => ({ ...o, [k]: t[k] || '' }), {}) });
  };
  const saveTeam = async () => {
    setErr('');
    try { await api.post('/brands/' + teamModal.brand.id + '/team', teamModal.form); setTeamModal(null); loadBrands(sel.id); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to save team'); }
  };

  return (
    <div style={{ padding: 24, background: C.pageBg, minHeight: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ margin: 0, color: C.sidebar, fontSize: 22 }}>Buyer Master</h2>
        <button onClick={() => { setErr(''); setNote(''); setBuyerModal({ __orig: {} }); }} style={btn(C.navy, '#fff')}>＋ Buyer</button>
      </div>
      <div style={{ width: 60, height: 3, background: C.red, borderRadius: 2, margin: '6px 0 18px' }} />
      {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{err}</div>}
      {/* THE SAVE SUCCEEDED AND SAID SOMETHING. Lowering a tolerance returns the count of
          lines already ordered above the new band — the orders stand and nothing recomputes,
          so this is amber and dismissible, not an error. It is the one case the warning
          exists for, and this screen used to throw the response away. */}
      {note && (
        <div style={{ background: '#FEF3C7', border: '1px solid #fde68a', color: '#854F0B', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ flex: 1 }}>{note}</span>
          <button onClick={() => setNote('')} style={{ background: 'transparent', border: 'none', color: '#854F0B', cursor: 'pointer', fontWeight: 700 }}>×</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16 }}>
        {/* Buyers */}
        <div style={{ background: '#fff', borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>Buyers ({buyers.length})</div>
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {buyers.map(b => (
              <div key={b.id} onClick={() => setSel(b)} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f3f3', background: sel?.id === b.id ? '#EEEDF8' : '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: sel?.id === b.id ? 700 : 500, color: C.text, fontSize: 13 }}>{b.name}</span>
                <button onClick={e => { e.stopPropagation(); setErr(''); setNote(''); setBuyerModal({ ...b, __orig: b }); }} style={{ background: 'transparent', border: 'none', color: C.navy, cursor: 'pointer', fontSize: 12 }}>✎</button>
              </div>
            ))}
            {buyers.length === 0 && <div style={{ padding: 16, color: '#999', fontSize: 13 }}>No buyers yet.</div>}
          </div>
        </div>

        {/* Brands of the selected buyer */}
        <div style={{ background: '#fff', borderRadius: 10, border: `1px solid ${C.border}`, padding: 16 }}>
          {!sel ? <div style={{ color: '#999', fontSize: 14, padding: 30, textAlign: 'center' }}>Select a buyer to manage its brands & teams.</div> : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.sidebar }}>{sel.name} — Brands</div>
                <button onClick={() => { setErr(''); setBrandModal({}); }} style={btn('#EEEDF8', C.navy)}>＋ Brand</button>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>{['Brand', 'Team', ''].map(h => <th key={h} style={{ textAlign: h === '' ? 'right' : 'left', padding: '8px 10px', fontSize: 10, color: '#888', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                <tbody>
                  {brands.map(br => (
                    <tr key={br.id}>
                      <td style={{ padding: '8px 10px', fontWeight: 600, borderBottom: '1px solid #f3f3f3' }}>{br.name}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid #f3f3f3' }}>
                        {br.has_team ? <span style={{ color: C.success, fontWeight: 600 }}>✓ assigned</span> : <span style={{ color: C.warning, fontWeight: 600 }}>⚠ no team</span>}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #f3f3f3', whiteSpace: 'nowrap' }}>
                        <button onClick={() => openTeam(br)} style={btn('#EEEDF8', C.navy)}>{br.has_team ? '✎ Team' : '+ Team'}</button>
                        <button onClick={() => { setErr(''); setBrandModal(br); }} style={{ ...btn('transparent', C.navy, `1px solid ${C.border}`), marginLeft: 6 }}>✎</button>
                      </td>
                    </tr>
                  ))}
                  {brands.length === 0 && <tr><td colSpan={3} style={{ padding: 20, color: '#999', textAlign: 'center' }}>No brands yet — add one.</td></tr>}
                </tbody>
              </table>
              <ListFooter shown={brands.length} total={brands.length} noun="brands" />
            </>
          )}
        </div>
      </div>

      {/* Buyer modal */}
      {buyerModal && (
        <Modal title={buyerModal.id ? 'Edit Buyer' : 'New Buyer'} onClose={() => setBuyerModal(null)}>
          {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: 8, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{err}</div>}
          <div style={{ display: 'grid', gap: 10 }}>
            {(() => {
              // One helper, so a new field is a line rather than a paragraph.
              // F is CALLED — F('name', …) — so it returns inline JSX and mints no component
              // type. Row and Head were RENDERED — <Row>…</Row> — which is what made them new
              // types on every keystroke. That is the whole distinction, and it is why this one
              // may stay local while those two had to move to module scope.
              const F = (key, label, extra = {}) => (
                <div><label style={labelStyle}>{label}</label>
                  <input style={inputStyle} {...extra} value={buyerModal[key] ?? ''}
                    onChange={e => setBuyerModal({ ...buyerModal, [key]: e.target.value })} /></div>);
              return (<>
                {F('name', 'Buyer Name *')}
                <Row>{F('contact_name', 'Contact Name')}{F('country', 'Country')}</Row>
                <Row>{F('contact_email', 'Contact Email')}{F('contact_phone', 'Contact Phone')}</Row>
                <Row>{F('gstin', 'GSTIN')}{F('website', 'Website')}</Row>

                <Head>Addresses</Head>
                {F('address', 'Registered address')}
                <Row>{F('billing_address', 'Billing address')}{F('delivery_address', 'Delivery address')}</Row>

                <Head>Commercial terms</Head>
                {/* payment_terms (text) and payment_terms_days sit ADJACENT on purpose. The
                    text field is what this form has always edited and is empty on all four
                    live buyers; payment_terms_days is set on all four and is what actually
                    moves money — logistics.routes.js:181 writes
                    sales_invoices.buyer_due_date = grc_date + payment_terms_days. Two fields
                    for one idea is a pre-existing redundancy; showing them side by side is
                    the honest way to leave it visible rather than deepen it. */}
                <Row>{F('payment_terms', 'Payment terms (free text)')}
                     {F('payment_terms_days', 'Payment terms — days', { type: 'number', min: 0 })}</Row>
                {/* FORWARD ONLY, and said so. buyer_due_date is stamped onto a sales invoice
                    once, at GRC confirmation (logistics.routes.js:181 — grc_date + days), and
                    nothing restamps it. Changing the number here cannot reach an invoice that
                    already carries a date. Worth knowing: 0 of 39 invoices carry one today, so
                    this has never actually run. */}
                <div style={{ fontSize: 11, color: '#888', lineHeight: 1.5, marginTop: -4 }}>
                  Days apply to invoices stamped from now. A due date is written once, at GRC
                  confirmation, and is never recalculated — invoices already stamped keep theirs.
                </div>
                <Row>{F('terms_basis', 'Terms basis')}
                     {F('discount_days', 'Discount days', { type: 'number', min: 0 })}</Row>
                {F('discounting_days', 'Discounting days', { type: 'number', min: 0 })}

                <Head>Quantity tolerance</Head>
                <div style={{ fontSize: 11, color: '#888', lineHeight: 1.5 }}>
                  The band this buyer accepts against the ordered quantity. Leave blank if no
                  allowance has been agreed — blank and 0 are different: 0 means agreed and exact.
                </div>
                <Row>{F('tolerance_over_pct', 'Accepts over (%)', { type: 'number', min: 0, max: 100, step: '0.01' })}
                     {F('tolerance_under_pct', 'Accepts under (%)', { type: 'number', min: 0, max: 100, step: '0.01' })}</Row>
                {/* CONDITIONAL, mirroring the route: a reason is demanded only when a tolerance
                    value has actually moved, so somebody editing a phone number is not asked
                    to justify it. Without this the save 400s with no box to answer in. */}
                {tolMoved(buyerModal) && (
                  <div>
                    <label style={labelStyle}>Reason for the tolerance change *</label>
                    <input style={inputStyle} autoFocus value={buyerModal.reason || ''}
                      placeholder="What the buyer agreed, and when"
                      onChange={e => setBuyerModal({ ...buyerModal, reason: e.target.value })} />
                    <div style={{ fontSize: 11, color: '#B45309', marginTop: 4 }}>
                      This is a term the buyer agreed. The record of why is the only control on it.
                    </div>
                  </div>
                )}
              </>);
            })()}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setBuyerModal(null)} style={btn('#f0f2f8', '#444', '1px solid #ddd')}>Cancel</button>
            <button onClick={saveBuyer} style={btn(C.navy, '#fff')}>Save</button>
          </div>
        </Modal>
      )}

      {/* Brand modal */}
      {brandModal && (
        <Modal title={brandModal.id ? 'Edit Brand' : `New Brand under ${sel?.name}`} onClose={() => setBrandModal(null)}>
          {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: 8, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{err}</div>}
          <div><label style={labelStyle}>Brand Name *</label><input style={inputStyle} value={brandModal.name || ''} onChange={e => setBrandModal({ ...brandModal, name: e.target.value })} placeholder="e.g. EASYBUY" /></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setBrandModal(null)} style={btn('#f0f2f8', '#444', '1px solid #ddd')}>Cancel</button>
            <button onClick={saveBrand} style={btn(C.navy, '#fff')}>Save</button>
          </div>
        </Modal>
      )}

      {/* Brand team modal */}
      {teamModal && (
        <Modal title={`Team — ${teamModal.brand.name}`} onClose={() => setTeamModal(null)}>
          {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: 8, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{err}</div>}
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>Every GPO under this brand will resolve its performance team from here (unless a per-GPO team overrides it).</div>
          {ROLES.map(([key, label, slot, nameKey]) => {
            const opts = slotUsers[slot] || [];
            const assignedId = teamModal.form[key];
            // Keep an already-assigned person visible even if their role is no
            // longer selectable (e.g. a legacy management assignment).
            const showKeep = assignedId && !opts.some(u => u.id === assignedId);
            const keepName = teamModal.row?.[nameKey];
            return (
            <div key={key} style={{ marginBottom: 11 }}>
              <label style={labelStyle}>{label}</label>
              <select style={inputStyle} value={teamModal.form[key] || ''} onChange={e => setTeamModal({ ...teamModal, form: { ...teamModal.form, [key]: e.target.value } })}>
                <option value="">— not assigned —</option>
                {showKeep && <option value={assignedId}>{keepName || '(currently assigned)'} — keep</option>}
                {opts.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setTeamModal(null)} style={btn('#f0f2f8', '#444', '1px solid #ddd')}>Cancel</button>
            <button onClick={saveTeam} style={btn(C.navy, '#fff')}>💾 Save Team</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
